/**
 * useCalendarAutoSync Hook
 * 
 * Integrates CalendarSyncService auto-sync with the app lifecycle.
 * Ensures sync always uses fresh events from the canonical store (Supabase),
 * not stale component state.
 * 
 * Usage:
 * - Mount in app root (e.g., AppShell or main.tsx)
 * - Auto-sync is OFF by default; call enableAutoSync() to turn on
 * - Automatically stops on logout or unmount
 * 
 * @module useCalendarAutoSync
 * @version 1.0.0
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { CalendarEventService } from '../services/CalendarEventService';
import { 
  startCalendarAutoSync, 
  stopCalendarAutoSync,
  getCalendarSyncState,
  type SyncServiceState 
} from '../integrations/CalendarSyncService';
import type { CalendarEvent } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Default auto-sync interval: 5 minutes.
 */
const DEFAULT_AUTO_SYNC_INTERVAL_MS = 5 * 60 * 1000;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface UseCalendarAutoSyncOptions {
  /** Sync interval in milliseconds (default: 5 minutes) */
  intervalMs?: number;
  /** Whether to auto-enable sync on mount (default: false - OFF by default) */
  autoEnable?: boolean;
}

export interface UseCalendarAutoSyncReturn {
  /** Whether auto-sync is currently enabled */
  isEnabled: boolean;
  /** Current sync service state */
  syncState: SyncServiceState | null;
  /** Enable auto-sync with fresh event getter */
  enableAutoSync: () => void;
  /** Disable auto-sync */
  disableAutoSync: () => void;
  /** Toggle auto-sync state */
  toggleAutoSync: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to manage calendar auto-sync lifecycle.
 * 
 * Key features:
 * - Uses CalendarEventService.getEvents() for fresh data from canonical store
 * - Never blocks Calendar page rendering (sync runs in background)
 * - Automatically stops on logout via 'ax1network-logout' event
 * - Cleans up on unmount
 * 
 * @example
 * // In AppShell or root component
 * const { isEnabled, enableAutoSync, disableAutoSync } = useCalendarAutoSync();
 * 
 * // In settings UI
 * <Toggle checked={isEnabled} onChange={isEnabled ? disableAutoSync : enableAutoSync} />
 */
export function useCalendarAutoSync(
  options: UseCalendarAutoSyncOptions = {}
): UseCalendarAutoSyncReturn {
  const { intervalMs = DEFAULT_AUTO_SYNC_INTERVAL_MS, autoEnable = false } = options;
  const { user } = useAuth();
  
  const [isEnabled, setIsEnabled] = useState(false);
  const [syncState, setSyncState] = useState<SyncServiceState | null>(null);
  
  // Track if we've started sync to avoid double-start
  const syncStartedRef = useRef(false);
  
  // Ref for refresh timer (to avoid window property and ESLint any warnings)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // ─────────────────────────────────────────────────────────────────────────
  // Fresh Events Getter
  // ─────────────────────────────────────────────────────────────────────────
  
  /**
   * Returns fresh events from the canonical store (Supabase).
   * This is called by CalendarSyncService on each auto-sync tick.
   * 
   * IMPORTANT: This fetches from the database, not from component state,
   * ensuring we always sync the latest data.
   */
  const getLatestEventsFromStore = useCallback((): CalendarEvent[] => {
    // For synchronous getter, we return an empty array initially
    // The actual fresh fetch happens asynchronously
    // This is a limitation of the current CalendarSyncService interface
    // which expects a synchronous getter
    
    // We'll use a cached approach: store the last fetched events
    // and trigger a fresh fetch that updates the cache
    return lastFetchedEventsRef.current;
  }, []);
  
  // Cache for last fetched events (updated by background fetcher)
  const lastFetchedEventsRef = useRef<CalendarEvent[]>([]);
  
  /**
   * Background fetcher that keeps the event cache fresh.
   * Runs slightly before each sync to ensure fresh data.
   */
  const refreshEventCache = useCallback(async () => {
    if (!user?.id) {
      lastFetchedEventsRef.current = [];
      return;
    }
    
    try {
      const service = CalendarEventService.getInstance();
      const result = await service.getEvents({
        ownerId: user.id,
        // Get events within a reasonable sync window (past month to next year)
        dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        dateTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        pageSize: 1000, // Get all events for sync
      });
      
      if (!result.error) {
        lastFetchedEventsRef.current = result.data;
      }
    } catch (err) {
      console.warn('[useCalendarAutoSync] Failed to refresh event cache:', err);
    }
  }, [user?.id]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // Enable/Disable Functions
  // ─────────────────────────────────────────────────────────────────────────
  
  const enableAutoSync = useCallback(() => {
    if (syncStartedRef.current) return;
    
    // Initial cache population
    refreshEventCache();
    
    // Start the auto-sync with our getter
    startCalendarAutoSync(getLatestEventsFromStore, intervalMs);
    syncStartedRef.current = true;
    setIsEnabled(true);
    
    // Also set up a pre-sync refresh interval (runs 30s before each sync)
    // This ensures cache is fresh when sync triggers
    const preRefreshInterval = Math.max(intervalMs - 30000, 60000);
    refreshTimerRef.current = setInterval(refreshEventCache, preRefreshInterval);
    
    console.log('[useCalendarAutoSync] Auto-sync enabled with', intervalMs / 1000, 's interval');
  }, [intervalMs, getLatestEventsFromStore, refreshEventCache]);
  
  const disableAutoSync = useCallback(() => {
    stopCalendarAutoSync();
    syncStartedRef.current = false;
    setIsEnabled(false);
    
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    
    console.log('[useCalendarAutoSync] Auto-sync disabled');
  }, []);
  
  const toggleAutoSync = useCallback(() => {
    if (isEnabled) {
      disableAutoSync();
    } else {
      enableAutoSync();
    }
  }, [isEnabled, enableAutoSync, disableAutoSync]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // State Polling
  // ─────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    if (!isEnabled) {
      setSyncState(null);
      return;
    }
    
    // Poll sync state every 10 seconds when enabled
    const pollState = () => {
      setSyncState(getCalendarSyncState());
    };
    
    pollState(); // Initial state
    const interval = setInterval(pollState, 10000);
    
    return () => clearInterval(interval);
  }, [isEnabled]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // Logout Cleanup
  // ─────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    const handleLogout = () => {
      console.log('[useCalendarAutoSync] Logout detected, stopping auto-sync');
      disableAutoSync();
      lastFetchedEventsRef.current = [];
    };
    
    window.addEventListener('ax1network-logout', handleLogout);
    
    return () => {
      window.removeEventListener('ax1network-logout', handleLogout);
    };
  }, [disableAutoSync]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // Unmount Cleanup
  // ─────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    return () => {
      if (syncStartedRef.current) {
        stopCalendarAutoSync();
        syncStartedRef.current = false;
        
        // Clear refresh timer
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      }
    };
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // Auto-enable (optional)
  // ─────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    if (autoEnable && user && !syncStartedRef.current) {
      enableAutoSync();
    }
  }, [autoEnable, user, enableAutoSync]);
  
  return {
    isEnabled,
    syncState,
    enableAutoSync,
    disableAutoSync,
    toggleAutoSync,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience Export
// ─────────────────────────────────────────────────────────────────────────────

export default useCalendarAutoSync;
